import API_CONFIG from '../config/api';
import { analytics } from '../utils/analytics';

// API Configuration
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/users/login',
  REGISTER: '/users/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  RESEND_VERIFICATION: '/auth/resend-verification',
  LOGOUT: '/users/logout',
  
  // User Profile
  USER_PROFILE: '/users/profile',
  UPDATE_PROFILE: '/users/update-profile',
  
  // Bug Tracking
  BUGS: '/application/bug-report',
  BUG_VOTE: '/application/bug-upvote/:id',
  
  // Feature Requests
  FEATURES: '/application/features',
  FEATURE_VOTE: '/application/features/:id',
  
  // Roadmap
  ROADMAP: '/roadmap',
  
  // Payments
  PAYMENT_STRIPE: '/payments/stripe-checkout',
  PAYMENT_PAYPAL: '/payments/paypal-checkout',
  PAYMENT_CRYPTO: '/payments/btcpay-invoice',
  PAYMENT_PAYPAL_CAPTURE: '/payments/paypal/orders/:orderId/capture',
  PAYMENT_HISTORY: '/payments/history',
  
  // Newsletter
  NEWSLETTER_SUBSCRIBE: '/newsletter/subscribe',
  
  // Contact
  CONTACT_FORM: '/contact',
};

export interface LoginRequest {
  username: string;
  password: string;
  origin: string;
}

export interface LoginResponse {
  token: string;
  username: string;
  email: string;
  avatar?: string;
  message: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  email: string;
  firstname: string;
  lastname: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ResendVerificationRequest {
  Email: string;
}

export interface BugSubmissionRequest {
  title: string;
  description: string;
  type: 'bug' | 'feature';
  priority?: 'low' | 'medium' | 'high';
  category?: string;
}

export interface BugResponse {
  id: number;
  title: string;
  description: string;
  votes: number;
  status: 'open' | 'in-progress' | 'testing' | 'fixed';
  type: 'bug' | 'feature';
  submittedBy: string;
  submittedDate: string;
  hasVoted: boolean;
  priority?: 'low' | 'medium' | 'high';
  category?: string;
}

export interface RoadmapItem {
  id: number;
  title: string;
  description: string;
  status: 'planned' | 'in-progress' | 'testing' | 'completed' | 'on-hold';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: 'feature' | 'improvement' | 'bugfix' | 'infrastructure';
  estimatedDate?: string;
  completedDate?: string;
  votes: number;
}

export interface RoadmapResponse {
  items: RoadmapItem[];
  totalCount: number;
  categories: string[];
  statuses: string[];
}

// Generic API Error
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// HTTP Client Class
class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  // Update base URL (useful for testing different environments)
  setBaseURL(url: string) {
    this.baseURL = url;
    if (API_CONFIG.DEBUG) {
      console.log(`API base URL updated to: ${url}`);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get auth token from localStorage
    const token = localStorage.getItem('doyenToken');
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const requestedResponseType = (options as any).responseType as
        | 'json'
        | 'text'
        | undefined;

      if (!response.ok) {
        let errorData: any = {};
        if (isJson) {
          errorData = await response.json().catch(() => ({}));
        } else {
          const textBody = await response.text().catch(() => '');
          errorData = { message: textBody || response.statusText };
        }
        analytics.trackApiError(endpoint, response.status, errorData.message || 'Unknown error');
        throw new ApiError(
          errorData.message || `HTTP Error: ${response.status}`,
          response.status,
          errorData.code
        );
      }

      let data: any;
      const responseType = requestedResponseType ?? (isJson ? 'json' : 'text');
      if (responseType === 'json') {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      // Track successful API calls for important endpoints
      if (endpoint.includes('/auth/') || endpoint.includes('/payments/') || endpoint.includes('/bugs/')) {
        analytics.trackFeatureUsage('api', `${options.method || 'GET'} ${endpoint}`);
      }
      
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout');
        }
        throw new ApiError(error.message);
      }
      
      throw new ApiError('Unknown error occurred');
    }
  }

  // GET request
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// API Service Functions
export const authAPI = {
  login: async (credentials: LoginRequest) => {
    try {
      const response = await apiClient.post<LoginResponse>(API_ENDPOINTS.LOGIN, credentials);
      console.log('[authAPI] response:', response);
      if (response) {
        analytics.trackLogin('username');
      }
      return response;
    } catch (error) {
      analytics.trackError('Login failed', 'authAPI.login');
      throw error;
    }
  },

  register: async (userData: RegisterRequest) => {
    try {
      const response = await apiClient.post<RegisterRequest>(API_ENDPOINTS.REGISTER, userData);
      if (response) {
        analytics.trackRegister();
      }
      return response;
    } catch (error) {
      analytics.trackError('Registration failed', 'authAPI.register');
      throw error;
    }
  },

  forgotPassword: async (data: ForgotPasswordRequest) => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.FORGOT_PASSWORD, data);
    if (response) {
      analytics.trackFeatureUsage('auth', 'forgot-password');
    }
    return response;
  },

  resetPassword: async (data: ResetPasswordRequest) => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.RESET_PASSWORD, data);
    if (response) {
      analytics.trackFeatureUsage('auth', 'reset-password');
    }
    return response;
  },

  resendVerification: async (data: ResendVerificationRequest) => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.RESEND_VERIFICATION, data);
    if (response) {
      analytics.trackFeatureUsage('auth', 'resend-verification');
    }
    return response;
  },

  logout: async () => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.LOGOUT);
    if (response) {
      analytics.trackLogout();
    }
    return response;
  }
};

export const bugAPI = {
  getBugs: () =>
    apiClient.get<BugResponse[]>(API_ENDPOINTS.BUGS),

  submitBug: async (data: BugSubmissionRequest) => {
    const response = await apiClient.post<BugResponse>(API_ENDPOINTS.BUGS, data);
    if (response) {
      analytics.trackBugReport('bug');
    }
    return response;
  },

  voteBug: async (bugId: number) => {
    const response = await apiClient.post<{ votes: number; hasVoted: boolean }>(
      API_ENDPOINTS.BUG_VOTE.replace(':id', bugId.toString())
    );
    if (response) {
      analytics.trackBugVote('bug', 'upvote');
    }
    return response;
  },
};

export const featureAPI = {
  getFeatures: () =>
    apiClient.get<BugResponse[]>(API_ENDPOINTS.FEATURES),

  submitFeature: async (data: BugSubmissionRequest) => {
    const response = await apiClient.post<BugResponse>(API_ENDPOINTS.FEATURES, data);
    if (response) {
      analytics.trackBugReport('feature');
    }
    return response;
  },

  voteFeature: async (featureId: number) => {
    const response = await apiClient.post<{ votes: number; hasVoted: boolean }>(
      API_ENDPOINTS.FEATURE_VOTE.replace(':id', featureId.toString())
    );
    if (response) {
      analytics.trackBugVote('feature', 'upvote');
    }
    return response;
  },
};

export const newsletterAPI = {
  subscribe: async (email: string) => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.NEWSLETTER_SUBSCRIBE, { email });
    if (response) {
      analytics.trackNewsletterSubscribe();
    }
    return response;
  },
};

export const contactAPI = {
  sendMessage: async (data: { name: string; email: string; message: string }) => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.CONTACT_FORM, data);
    if (response) {
      analytics.trackContactForm();
    }
    return response;
  },
};

export const roadmapAPI = {
  getRoadmap: async (status?: string, category?: string) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    
    const endpoint = params.toString() ? `${API_ENDPOINTS.ROADMAP}?${params}` : API_ENDPOINTS.ROADMAP;
    const response = await apiClient.get<RoadmapResponse>(endpoint);
    
    if (response) {
      analytics.trackFeatureUsage('roadmap', 'view');
    }
    return response;
  },

  getRoadmapItem: async() => { //(itemId: number) => {
    /*const response = await apiClient.get<RoadmapItem>(
      API_ENDPOINTS.ROADMAP_ITEM.replace(':id', itemId.toString())
    );
    
    if (response.success) {
      analytics.trackFeatureUsage('roadmap', 'view-item');
    }
    return response;*/
    return null; // Placeholder for future implementation
  },

  voteRoadmapItem: async() => { //(itemId: number) => {
    /*const response = await apiClient.post<{ votes: number; hasVoted: boolean }>(
      `${API_ENDPOINTS.ROADMAP_ITEM.replace(':id', itemId.toString())}/vote`
    );
    
    if (response.success) {
      analytics.trackFeatureUsage('roadmap', 'vote');
    }
    return response;*/
    return null; // Placeholder for future implementation
  },
};

// Utility function to update API base URL
export const setApiBaseURL = (url: string) => {
  apiClient.setBaseURL(url);
};

// Export client for custom requests
export { apiClient as api };
